// Quote Builder — global resume banner.
//
// Verbatim port of source `showResumeBanner` + `dismissResumeBanner`
// (kos-qb-v3.js:2318–2341). Mounts a banner to `document.body` when
// `kos_qb_v3_minimised` LS flag is set AND the cached quote has content
// (customer name or any line items). Click "Resume" → navigates to
// /quote-builder; click × → clears the flag and removes the banner.
//
// The banner re-checks after every URL hash change so leaving the QB
// surfaces it on the dashboard immediately.
//
// Mounted as a side-effect from main.ts.
//
// Defraud / Stripe / commission: no touching. This module reads LS only.

import { MINIMISED_FLAG } from "./state";
import { fmtPence, svgIcon } from "./formatters";
import { quoteTotals } from "./pricing";
import type { QuoteBuilderState } from "./types";

const QUOTE_LS_KEY = "kos_quote_builder_v3";
const BANNER_ID = "qb-resume-banner";

function readQuoteFromLs(): QuoteBuilderState | null {
  try {
    const raw = localStorage.getItem(QUOTE_LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as QuoteBuilderState;
  } catch {
    return null;
  }
}

function lineCountOf(q: QuoteBuilderState): number {
  let n = 0;
  for (const z of q.zones ?? []) n += (z.items ?? []).length;
  return n;
}

function hasContent(q: QuoteBuilderState): boolean {
  return Boolean(q.customer) || lineCountOf(q) > 0;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function navigateToQb(): void {
  // Router uses `#/page-name`; navigate by setting location.hash.
  // The shell's router picks up the change.
  window.location.hash = "#/quote-builder";
}

function clearFlag(): void {
  try { localStorage.removeItem(MINIMISED_FLAG); } catch { /* ignore */ }
}

function removeBanner(): void {
  const el = document.getElementById(BANNER_ID);
  if (el) el.remove();
}

function showResumeBanner(): void {
  // Already mounted? Skip — source's showResumeBanner does the same
  // (line 2319: `if (document.getElementById('kos-qb-resume-banner')) return;`).
  if (document.getElementById(BANNER_ID)) return;

  // Skip if we're already ON the QB page — banner is for "elsewhere".
  if (window.location.hash.startsWith("#/quote-builder")) return;

  // Skip if the flag isn't set.
  let minimised = false;
  try { minimised = !!localStorage.getItem(MINIMISED_FLAG); } catch { /* ignore */ }
  if (!minimised) return;

  const q = readQuoteFromLs();
  if (!q || !hasContent(q)) return;

  const t = quoteTotals(q);
  const customerBit = q.customer ? ` for ${escapeHtml(q.customer)}` : "";
  const lineCount = t.lineCount;
  const lines = `${lineCount} line${lineCount === 1 ? "" : "s"}`;

  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.className = "qb-resume-banner";
  banner.innerHTML = `
    <div class="qb-resume-banner__ic">${svgIcon("M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8|M14 2v6h6|M9 13h6|M9 17h6")}</div>
    <div class="qb-resume-banner__tx">
      <strong>Resume quote${customerBit}</strong> · ${lines} · <em>${fmtPence(t.grandTotalPence)}</em>
    </div>
    <button type="button" class="qb-btn qb-btn--primary" data-role="resume">Resume</button>
    <button type="button" class="qb-resume-banner__dismiss" data-role="dismiss" aria-label="Dismiss">×</button>
  `;
  banner.querySelector<HTMLButtonElement>("[data-role='resume']")?.addEventListener("click", () => {
    // Clear flag before navigating — the QB page also clears on mount.
    clearFlag();
    removeBanner();
    navigateToQb();
  });
  banner.querySelector<HTMLButtonElement>("[data-role='dismiss']")?.addEventListener("click", () => {
    clearFlag();
    removeBanner();
  });
  document.body.appendChild(banner);
}

/** Install the global resume-banner watcher. Idempotent. */
export function installResumeBanner(): void {
  // First check on load.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", showResumeBanner);
  } else {
    showResumeBanner();
  }
  // Re-check whenever the page route changes.
  window.addEventListener("hashchange", () => {
    if (window.location.hash.startsWith("#/quote-builder")) {
      removeBanner();
    } else {
      // Defer one tick so the cleanup of the QB page can set/clear the
      // flag before we re-read it.
      window.setTimeout(showResumeBanner, 50);
    }
  });
}
